// Business Creation Form Validation Logic
import { isValidTimezone } from "@finitless/shared/lib";
export const validateStep = (
  step,
  formData,
  selectedPaymentMethods,
  schedule,
) => {
  const errors = {};

  switch (step) {
    case 1:
      // Basic Information validation
      if (!formData.name?.trim()) {
        errors.name = "Business name is required";
      }
      if (!formData.email?.trim()) {
        errors.email = "Email is required";
      } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
        errors.email = "Valid email is required";
      }
      // Validate phone number if provided
      if (!formData.cellphone?.trim()) {
        errors.phone = "Phone number is required";
      } else if (!/^\d{1,15}$/.test(formData.cellphone)) {
        errors.phone = "Phone number must contain only numbers (max 15 digits)";
      }
      break;

    case 2:
      // Location & Address validation
      if (!formData.address?.trim()) {
        errors.address = "Address is required";
      }
      if (!formData.location?.lat || !formData.location?.lng) {
        errors.location = "Valid location is required";
      }
      // Validate coordinates are numbers
      if (
        formData.location?.lat &&
        (isNaN(formData.location.lat) ||
          formData.location.lat < -90 ||
          formData.location.lat > 90)
      ) {
        errors.location = "Invalid latitude value";
      }
      if (
        formData.location?.lng &&
        (isNaN(formData.location.lng) ||
          formData.location.lng < -180 ||
          formData.location.lng > 180)
      ) {
        errors.location = "Invalid longitude value";
      }
      // Validate timezone
      if (!formData.timezone?.trim()) {
        errors.timezone = "Timezone is required";
      } else if (!isValidTimezone(formData.timezone)) {
        errors.timezone = "Invalid timezone format";
      }
      break;

    case 3:
      // Payment Methods validation
      if (!formData.price_level) {
        errors.price_level = "Price level is required";
      }
      if (selectedPaymentMethods.length === 0) {
        errors.paymentMethods = "At least one payment method is required";
      }
      break;

    case 4:
      // Business Hours validation
      // If schedule is empty, allow advancing (default schedule will be applied later)
      if (schedule && schedule.length > 0) {
        // Check if at least one day is enabled
        const hasEnabledDay = schedule.some((day) => day.enabled);
        if (!hasEnabledDay) {
          errors.schedule = "At least one day must be enabled";
        } else {
          // Validate each enabled day's schedule
          const scheduleErrors = [];
          schedule.forEach((day, dayIndex) => {
            if (day.enabled && day.lapses) {
              // Check if there are any lapses
              if (day.lapses.length === 0) {
                scheduleErrors.push(
                  `${getDayName(dayIndex)} has no time periods configured`,
                );
              } else {
                // Validate each lapse
                day.lapses.forEach((lapse, lapseIndex) => {
                  if (!lapse.open || !lapse.close) {
                    scheduleErrors.push(
                      `${getDayName(dayIndex)} lapse ${lapseIndex + 1} is incomplete`,
                    );
                  } else {
                    const openMinutes =
                      lapse.open.hour * 60 + lapse.open.minute;
                    const closeMinutes =
                      lapse.close.hour * 60 + lapse.close.minute;

                    if (openMinutes >= closeMinutes) {
                      scheduleErrors.push(
                        `${getDayName(dayIndex)} lapse ${lapseIndex + 1}: opening time must be before closing time`,
                      );
                    }
                  }
                });

                // Check for overlapping lapses
                if (day.lapses.length > 1) {
                  const sortedLapses = [...day.lapses].sort((a, b) => {
                    const aMinutes = a.open.hour * 60 + a.open.minute;
                    const bMinutes = b.open.hour * 60 + b.open.minute;
                    return aMinutes - bMinutes;
                  });

                  for (let i = 0; i < sortedLapses.length - 1; i++) {
                    const current = sortedLapses[i];
                    const next = sortedLapses[i + 1];
                    const currentEndMinutes =
                      current.close.hour * 60 + current.close.minute;
                    const nextStartMinutes =
                      next.open.hour * 60 + next.open.minute;

                    if (currentEndMinutes >= nextStartMinutes) {
                      scheduleErrors.push(
                        `${getDayName(dayIndex)}: time periods cannot overlap`,
                      );
                      break;
                    }
                  }
                }
              }
            }
          });

          if (scheduleErrors.length > 0) {
            errors.schedule = scheduleErrors.join(". ");
          }
        }
      }
      break;

    case 5:
      // Photos step - no validation required
      break;

    case 6: {
      // Final review - validate all previous steps
      const allErrors = {};

      // Re-validate all steps
      for (let i = 1; i <= 5; i++) {
        const stepErrors = validateStepInternal(
          i,
          formData,
          selectedPaymentMethods,
          schedule,
        );
        Object.assign(allErrors, stepErrors);
      }

      Object.assign(errors, allErrors);
      break;
    }

    default:
      break;
  }

  return errors;
};

// Helper function to validate individual steps without setting errors
const validateStepInternal = (
  step,
  formData,
  selectedPaymentMethods,
  schedule,
) => {
  const errors = {};

  switch (step) {
    case 1:
      if (!formData.name?.trim()) errors.name = "Business name is required";
      if (!formData.email?.trim()) {
        errors.email = "Email is required";
      } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
        errors.email = "Valid email is required";
      }
      if (!formData.cellphone?.trim()) {
        errors.phone = "Phone number is required";
      } else if (!/^\d{1,15}$/.test(formData.cellphone)) {
        errors.phone = "Phone number must contain only numbers (max 15 digits)";
      }
      break;
    case 2:
      if (!formData.address?.trim()) errors.address = "Address is required";
      if (!formData.location?.lat || !formData.location?.lng)
        errors.location = "Valid location is required";
      if (!formData.timezone?.trim()) {
        errors.timezone = "Timezone is required";
      } else if (!isValidTimezone(formData.timezone)) {
        errors.timezone = "Invalid timezone format";
      }
      break;
    case 3:
      if (!formData.price_level) errors.price_level = "Price level is required";
      if (selectedPaymentMethods.length === 0)
        errors.paymentMethods = "At least one payment method is required";
      break;
    case 4:
      // Allow empty schedule; default will be used on submission/advance
      if (schedule && schedule.length > 0) {
        const hasEnabledDay = schedule.some((day) => day.enabled);
        if (!hasEnabledDay) {
          errors.schedule = "At least one day must be enabled";
        }
      }
      break;
  }

  return errors;
};

// Helper function to get day name
const getDayName = (dayIndex) => {
  const days = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ];
  return days[dayIndex] || `Day ${dayIndex + 1}`;
};

// Validate entire form
export const validateEntireForm = (
  formData,
  selectedPaymentMethods,
  schedule,
) => {
  const allErrors = {};

  // Validate all steps
  for (let i = 1; i <= 6; i++) {
    const stepErrors = validateStepInternal(
      i,
      formData,
      selectedPaymentMethods,
      schedule,
    );
    Object.assign(allErrors, stepErrors);
  }

  return allErrors;
};
